/**
 * useNow — the CRM's ONE clock (D-9, perf hardening).
 *
 * Before this hook every surface that shows a live duration owned a private
 * `setInterval(() => setNow(Date.now()), 1000)`. On a busy console that is half
 * a dozen independent 1s timers, each one re-rendering its whole subtree, and
 * each one still ticking on a backgrounded tab.
 *
 * This module keeps ONE `setInterval` for the whole app, started on the first
 * subscriber and cleared when the last one leaves (so jsdom tests — and idle
 * pages — hold no timer at all). Every subscriber is notified on the same 1s
 * tick, but a consumer only re-renders when the value AT ITS OWN GRANULARITY
 * changes: a `useNow(15000)` consumer re-renders once per fifteen ticks, not
 * fifteen times.
 *
 * The returned value is `Date.now()` FLOORED to the granularity. That is what
 * makes "re-render only when my value changes" well-defined, and it means two
 * consumers at the same granularity always agree on the instant.
 *
 * `enabled` (optional, default true) keeps the pre-existing "don't tick while
 * idle" behaviour of surfaces that mounted their interval conditionally — a
 * disabled consumer holds no subscription and therefore cannot keep the shared
 * timer alive.
 */
import { useEffect, useRef, useState } from 'react';

/** The two cadences the CRM actually displays: seconds, and coarse SLA chips. */
export type NowGranularity = 1000 | 15000;

type Listener = () => void;

const listeners = new Set<Listener>();
let timerId: ReturnType<typeof setInterval> | null = null;
/** Newest wall-clock reading, refreshed by the one interval below. */
let latestNow = Date.now();

const floorTo = (at: number, granularityMs: number): number =>
  Math.floor(at / granularityMs) * granularityMs;

function tick(): void {
  latestNow = Date.now();
  // Iterate a copy: a listener that unsubscribes while being notified must not
  // make the Set skip one of its siblings.
  for (const listener of Array.from(listeners)) listener();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  if (listeners.size === 1 && timerId === null) {
    latestNow = Date.now();
    timerId = setInterval(tick, 1000);
  }
  return () => {
    if (!listeners.delete(listener)) return;
    if (listeners.size === 0 && timerId !== null) {
      clearInterval(timerId);
      timerId = null;
    }
  };
}

/**
 * `Date.now()` floored to `granularityMs`, re-rendering the caller only when
 * that floored value changes.
 */
export function useNow(granularityMs: NowGranularity, enabled: boolean = true): number {
  const [value, setValue] = useState(() => floorTo(Date.now(), granularityMs));
  // Mirrors `value` so the subscription can decide whether a tick is a no-op
  // without re-subscribing on every render.
  const valueRef = useRef(value);

  useEffect(() => {
    if (!enabled) return undefined;
    const sync = (at: number) => {
      const next = floorTo(at, granularityMs);
      if (next === valueRef.current) return; // same bucket — no re-render
      valueRef.current = next;
      setValue(next);
    };
    // Mount / re-enable / granularity change: adopt the current instant before
    // waiting on the shared tick.
    sync(Date.now());
    return subscribe(() => sync(latestNow));
  }, [granularityMs, enabled]);

  return value;
}

export default useNow;
